'use es6';

import { Promise } from '../helpers/promise';
import { set } from '../helpers/update';
import { withResponseHandlers, buildResponse, buildErrorResponse, withRetry, withTracking, trackSuccess, trackFailureBasedOnErrorResponse, buildRequestError } from './adapterUtils';
import { withStaticAppInfo, ensureStaticAppInfo } from '../middlewares/staticAppInfo';
import { createStack } from '../index';
import { reportStatusCode } from '../tracking/trackRequests';

var maybeWithIframeXMLHttpRequest = function maybeWithIframeXMLHttpRequest(options) {
  var canUseSyncIframeRequest = options.useIframeRequest && window.iframeXMLHttpRequest && window.apiIframe && window.apiIframe.contentDocument;
  var canUseAsyncIframeRequest = options.useIframeRequest && window.iframeXMLHttpRequestPromise;
  var canUseIframeHack = canUseSyncIframeRequest || canUseAsyncIframeRequest;

  if (!canUseIframeHack) {
    return set('Request', XMLHttpRequest)(options);
  }

  var newHeaders = Object.assign({
    'X-HS-Referer': window.location.href
  }, options.headers);
  var optionsWithAdditionalHeaders = withStaticAppInfo(set('headers', newHeaders)(options)); // If iframeXMLHttpRequestPromise is set, wait for it to resolve before issuing a request
  // This is done when all appropriate requests must be sent via the frame

  if (canUseAsyncIframeRequest) {
    return window.iframeXMLHttpRequestPromise.then(function (iframeXMLHttpRequest) {
      return set('Request', iframeXMLHttpRequest)(optionsWithAdditionalHeaders);
    }).catch(function () {
      return set('Request', XMLHttpRequest)(options);
    });
  }

  return set('Request', window.iframeXMLHttpRequest)(optionsWithAdditionalHeaders);
};

var withOptions = function withOptions(options) {
  return new Promise(function (resolve) {
    var Request = options.Request || XMLHttpRequest;
    var xhr = new Request();

    if (options.error) {
      resolve(withResponseHandlers(buildErrorResponse(xhr, options.error.message, 'OPTIONSERROR'), options));
      return;
    } // the http request was done by a separate client and is being piped back
    // into this one for response handling


    if (options.externalResponse) {
      var fromExternalResponse = options.externalResponse instanceof XMLHttpRequest ? buildResponse(options.externalResponse) : Object.assign(buildResponse(xhr), options.externalResponse);
      resolve(withResponseHandlers(fromExternalResponse, options));
      return;
    }

    xhr.open(options.method || 'GET', options.url, true);

    if (typeof options.timeout === 'number') {
      xhr.timeout = options.timeout;
    }

    xhr.withCredentials = options.withCredentials;

    if (options.responseType) {
      xhr.responseType = options.responseType;
    }

    if (typeof options.withXhr === 'function') {
      options.withXhr(xhr);
    }

    Object.keys(options.headers || {}).forEach(function (headerName) {
      if (options.headers[headerName] !== false) {
        xhr.setRequestHeader(headerName, options.headers[headerName]);
      }
    });
    var sendTime = performance.now();
    xhr.addEventListener('load', function () {
      reportStatusCode({
        url: xhr.responseURL,
        sendTime: sendTime,
        statusCode: xhr.status
      });
      return resolve(withResponseHandlers(buildResponse(xhr), options));
    });
    xhr.addEventListener('error', function () {
      reportStatusCode({
        url: xhr.responseURL,
        sendTime: sendTime,
        statusCode: xhr.status
      });
      return resolve(withResponseHandlers(buildErrorResponse(xhr, 'Network request failed', 'NETWORKERROR'), options));
    });
    xhr.addEventListener('timeout', function () {
      reportStatusCode({
        url: xhr.responseURL,
        sendTime: sendTime,
        statusCode: xhr.status
      });
      return resolve(withResponseHandlers(buildErrorResponse(xhr, 'Request timeout', 'TIMEOUT'), options));
    });
    xhr.addEventListener('abort', function () {
      reportStatusCode({
        url: xhr.responseURL,
        sendTime: sendTime,
        statusCode: xhr.status
      });
      return resolve(withResponseHandlers(buildErrorResponse(xhr, 'Request aborted', 'ABORT'), options));
    });
    xhr.send(typeof options.data === 'undefined' ? null : options.data);
  });
};

var handleRequestErrors = function handleRequestErrors(reason) {
  return Promise.reject(buildRequestError(reason));
};

var essentialMiddleware = createStack(withTracking, maybeWithIframeXMLHttpRequest, ensureStaticAppInfo);
export default (function (optionMiddleware) {
  var client = function client(url, options) {
    return withRetry(Object.assign({}, options, {
      url: url
    }), function (o) {
      return optionMiddleware(o).catch(handleRequestErrors).then(essentialMiddleware).then(withOptions);
    }).then(trackSuccess, function (response) {
      return Promise.reject(trackFailureBasedOnErrorResponse(response));
    });
  };

  var responseWithMethod = function responseWithMethod(method) {
    return function (url, options) {
      return client(url, Object.assign({}, options, {
        method: method
      }));
    };
  };

  var withMethod = function withMethod(method) {
    return function (url, options) {
      return responseWithMethod(method)(url, options).then(function (_ref) {
        var data = _ref.data;
        return data;
      });
    };
  };

  return Object.assign(client, {
    get: withMethod('GET'),
    post: withMethod('POST'),
    put: withMethod('PUT'),
    patch: withMethod('PATCH'),
    delete: withMethod('DELETE'),
    options: withMethod('OPTIONS'),
    getWithResponse: responseWithMethod('GET'),
    postWithResponse: responseWithMethod('POST'),
    putWithResponse: responseWithMethod('PUT'),
    patchWithResponse: responseWithMethod('PATCH'),
    deleteWithResponse: responseWithMethod('DELETE'),
    optionsWithResponse: responseWithMethod('OPTIONS')
  });
});